import { useEffect, useMemo, useState } from "react";
import {
  Box, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Switch, FormControlLabel, Autocomplete, Alert, CircularProgress, Typography,
} from "@mui/material";
import { ACCENT } from "./accent";
import { DRONE_FIELDS, buildPayloadFromFields } from "./partSpecs";
import { createDrone } from "./api/propulsion";

const motorLabel = (m) => `${m.brand} ${m.name}${m.kv ? ` · ${m.kv}Kv` : ""}`;

// Dialog to add a standard drone and associate the motors that fit it.
// `motors` is the catalog motor list (from getCatalog) for the multi-select.
const AddDroneDialog = ({ open, motors = [], onClose, onCreated }) => {
  const [values, setValues] = useState({});
  const [selectedMotors, setSelectedMotors] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setValues({});
      setSelectedMotors([]);
      setError(null);
      setSaving(false);
    }
  }, [open]);

  const canSave = useMemo(
    () => DRONE_FIELDS.filter((f) => f.required).every((f) => String(values[f.key] || "").trim()),
    [values]
  );

  const set = (field) => (e) =>
    setValues((v) => ({ ...v, [field.key]: field.type === "bool" ? e.target.checked : e.target.value }));

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = {
        ...buildPayloadFromFields(DRONE_FIELDS, values),
        motor_ids: selectedMotors.map((m) => m.id),
      };
      const saved = await createDrone(payload);
      onCreated?.(saved);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 700 }}>Add drone</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Brand and name are required. Associate the motors known to fit this airframe.
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
          {DRONE_FIELDS.map((f) =>
            f.type === "bool" ? (
              <FormControlLabel
                key={f.key}
                sx={{ flex: "1 1 100%" }}
                control={<Switch checked={Boolean(values[f.key])} onChange={set(f)} size="small" />}
                label={<Typography variant="body2">{f.label}</Typography>}
              />
            ) : (
              <TextField
                key={f.key}
                sx={{ flex: f.key === "name" || f.key === "notes" ? "1 1 100%" : "1 1 200px", minWidth: 180 }}
                size="small"
                label={f.label}
                required={f.required}
                type={f.type === "number" ? "number" : "text"}
                value={values[f.key] ?? ""}
                onChange={set(f)}
              />
            )
          )}
          <Autocomplete
            sx={{ flex: "1 1 100%" }}
            multiple
            size="small"
            options={motors}
            value={selectedMotors}
            onChange={(_, v) => setSelectedMotors(v)}
            getOptionLabel={motorLabel}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            renderInput={(params) => (
              <TextField {...params} label="Associated motors" placeholder="Add motors…" />
            )}
          />
        </Box>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          onClick={save}
          disabled={saving || !canSave}
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ backgroundColor: ACCENT, textTransform: "none", "&:hover": { backgroundColor: "#0058A5" } }}
        >
          Add drone
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddDroneDialog;
